import * as React from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';

import {
    Accordion, AccordionDetails, AccordionSummary, Box,
    CircularProgress, IconButton, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import EvolutionTour1Tour2 from "./components/EvolutionTour1Tour2";
import AnalyseElectionParCandidat from "./components/AnalyseElectionParCandidat";

import PersonnasHighestAndDynamic from "./components/PersonnasHighestAndDynamic";
import {correctName, formatPercentage, getNomElection, tddDelay, tours} from "../../Utils";
import {useNavigate} from "react-router-dom";
import AnalyseElectionSerieLongue from "./components/AnalyseElectionSerieLongue";


export default function SouffletsAnalyse({
                                             forceClose,
                                             forceOpen,
                                             data,
                                             resultats,
                                             resultsParCandidatsNuancesCommune,
                                             mapUrl,
                                             showPersonae,
                                             resultsGeomIrisByBvsSynthese,
                                             resultsGeomByBvsSynthese,
                                             otherResults,
                                             showIris,
                                             bvId
                                         }) {
    let navigate = useNavigate();

    const trancheAge = {
        "Pop 0-14 ans": {
            "Mobilisation électorale": "— (non votants)",
            "Nuances du vote": "—",
            "Tendance politique": "—",
            "Tendance de vote": "Impact indirect via les parents (30–49 ans), variable selon CSP",
            "Besoins": "Éducation, santé, modes de garde, infrastructures scolaires et de loisirs",
            "Valeurs dominantes": "Protection de l’enfance, égalité des chances, solidarité intergénérationnelle"
        },
        "Pop 15-29 ans": {
            "Mobilisation électorale": "Faible à moyenne (forte abstention des 18–24 ans)",
            "Nuances du vote": "Gauche radicale, écologie, abstention, protestation",
            "Tendance politique": "Sensibilité à LFI, EELV, parfois RN (selon diplôme et territoire)",
            "Tendance de vote": "Montée de l’abstention, vote contestataire urbain",
            "Besoins": "Études, logement, emploi, mobilité, justice sociale, climat",
            "Valeurs dominantes": "Engagement, liberté, solidarité, écologie, défiance envers les institutions"
        },
        "Pop 30-44 ans": {
            "Mobilisation électorale": "Moyenne",
            "Nuances du vote": "Centre, écologie, RN dans les zones en tension",
            "Tendance politique": "Vote éclaté : Renaissance, LFI, RN, EELV",
            "Tendance de vote": "Forte fragmentation, influence du niveau de diplôme et du contexte local",
            "Besoins": "Conciliation vie pro/famille, logement, école, fiscalité",
            "Valeurs dominantes": "Méritocratie, sécurité économique, transmission, égalité des droits"
        },
        "Pop 45-59 ans": {
            "Mobilisation électorale": "Moyenne à forte",
            "Nuances du vote": "Droite modérée, RN, centre",
            "Tendance politique": "Tend vers LR, RN, Renaissance selon le statut socio-économique",
            "Tendance de vote": "Stabilité, droitisation progressive",
            "Besoins": "Emploi, retraite future, pouvoir d’achat, santé, fiscalité",
            "Valeurs dominantes": "Stabilité, mérite, autorité, effort, famille"
        },
        "Pop 60-74 ans": {
            "Mobilisation électorale": "Très forte",
            "Nuances du vote": "Droite modérée, conservatisme",
            "Tendance politique": "Fidèle à LR, Renaissance, RN (selon niveau de revenu)",
            "Tendance de vote": "Poids électoral majeur, très conservateur",
            "Besoins": "Santé, retraite, stabilité, sécurité",
            "Valeurs dominantes": "Ordre, transmission, sécurité, attachement aux valeurs traditionnelles"
        },
        "Pop 75 ans et plus": {
            "Mobilisation électorale": "Très forte",
            "Nuances du vote": "Droite conservatrice, centre droit",
            "Tendance politique": "Très stable électoralement : LR, Renaissance, parfois RN chez les plus modestes",
            "Tendance de vote": "Conservatisme dominant, poids électoral fort",
            "Besoins": "Dépendance, soins, maintien à domicile, accompagnement social",
            "Valeurs dominantes": "Ordre, solidarité familiale, prudence, continuité républicaine"
        }
    }
    const statusOcc = {
        "Propriétaires": {
            "Mobilisation électorale": "Forte à très forte",
            "Nuances du vote": "Droite modérée, centre, parfois RN ou LR",
            "Tendance politique": "Plutôt stable électoralement, conservateur sur les questions fiscales",
            "Tendance de vote": "Majorité LR/Renaissance, percées RN chez les petits propriétaires précaires",
            "Besoins": "Fiscalité, sécurité, cadre de vie, valorisation du patrimoine",
            "Valeurs dominantes": "Stabilité, mérite, autonomie, protection du capital acquis"
        },
        "Loc. Parc Privé": {
            "Mobilisation électorale": "Moyenne",
            "Nuances du vote": "Vote partagé entre RN, LFI, Renaissance",
            "Tendance politique": "Volatilité selon âge, niveau de vie et territoire",
            "Tendance de vote": "Fragmentation forte, progression du RN et LFI",
            "Besoins": "Loyers, pouvoir d’achat, accès à la propriété",
            "Valeurs dominantes": "Reconnaissance, sécurité économique, accès à la mobilité résidentielle"
        },
        "Loc. Parc Social": {
            "Mobilisation électorale": "Faible",
            "Nuances du vote": "LFI, RN, abstention forte",
            "Tendance politique": "Vote protestataire très marqué",
            "Tendance de vote": "LFI et RN selon profil ; très forte abstention",
            "Besoins": "Services publics, logement de qualité, sécurité, mixité sociale",
            "Valeurs dominantes": "Justice sociale, défiance, attente de reconnaissance, attachement local"
        },
        "Logé gratuit": {
            "Mobilisation électorale": "Très faible",
            "Nuances du vote": "Abstention massive, profils jeunes ou précaires",
            "Tendance politique": "Insertion difficile dans les schémas politiques classiques",
            "Tendance de vote": "Très faible participation, vote LFI ou RN marginalement",
            "Besoins": "Stabilité, autonomie, emploi, logement stable",
            "Valeurs dominantes": "Défiance, survie, aide sociale, aspiration à l’émancipation"
        }
    }

    const status = {
        "Actifs occupés": {
            "Mobilisation électorale": "Moyenne à forte",
            "Nuances du vote": "Vote éclaté : centre, droite modérée, écologie ou RN selon catégorie pro",
            "Tendance politique": "Vote structuré selon CSP : RN/LFI chez ouvriers et employés ; Renaissance/LR chez cadres",
            "Tendance de vote": "Polarisation croissante, vote utilitaire ou contestataire",
            "Besoins": "Sécurité de l’emploi, mobilité, logement, fiscalité",
            "Valeurs dominantes": "Méritocratie, stabilité, autonomie, sécurité économique"
        },
        "Inactifs": {
            "Mobilisation électorale": "Faible",
            "Nuances du vote": "LFI, RN, abstention très élevée",
            "Tendance politique": "Forte défiance, éloignement du jeu politique classique",
            "Tendance de vote": "LFI chez les jeunes diplômés, RN chez les précaires, forte abstention",
            "Besoins": "Aide sociale, emploi, logement, reconnaissance sociale",
            "Valeurs dominantes": "Justice sociale, survie, défiance, besoin de réinclusion"
        },
        "Chômeurs": {
            "Mobilisation électorale": "Faible à très faible",
            "Nuances du vote": "RN, LFI, abstention protestataire",
            "Tendance politique": "Rejet du système, vote de colère ou résignation",
            "Tendance de vote": "RN très fort, LFI chez les plus jeunes, très peu de vote modéré",
            "Besoins": "Emploi, reconnaissance, dignité, accompagnement",
            "Valeurs dominantes": "Sécurité, colère sociale, sentiment d’abandon, espoir de changement"
        }
    }
    const periodeEmmenagement = {
        "Ménages emménagés moins 2 ans": {
            "Mobilisation électorale": "Faible",
            "Nuances du vote": "Abstention forte, vote protestataire",
            "Tendance politique": "Déconnexion des enjeux locaux, faible identification politique",
            "Tendance de vote": "Fort taux d’abstention, vote LFI ou RN chez les précaires",
            "Besoins": "Intégration locale, repères, services de proximité, stabilité",
            "Valeurs dominantes": "Mobilité, adaptation, incertitude, individualisme"
        },
        "Ménages emménagés entre 2-4 ans": {
            "Mobilisation électorale": "Moyenne",
            "Nuances du vote": "Vote diversifié, RN, LFI, centre",
            "Tendance politique": "Recherche de repères, prise progressive de position politique",
            "Tendance de vote": "Volatilité électorale, début d’ancrage local",
            "Besoins": "École, logement durable, emploi, sécurité",
            "Valeurs dominantes": "Recherche de stabilité, projection, évaluation locale"
        },
        "Ménages emménagés entre 5-9 ans": {
            "Mobilisation électorale": "Moyenne à forte",
            "Nuances du vote": "Vote plus structuré, droite modérée, centre, écologie",
            "Tendance politique": "Début d’ancrage, rapport accru aux politiques locales",
            "Tendance de vote": "Participation en hausse, vote plus rationnel",
            "Besoins": "Services publics, fiscalité locale, cadre de vie",
            "Valeurs dominantes": "Méritocratie, attachement territorial, responsabilité"
        },
        "Ménages emménagés depuis 10+ ans": {
            "Mobilisation électorale": "Très forte",
            "Nuances du vote": "Vote ancré, droite traditionnelle, PS ou RN selon profil",
            "Tendance politique": "Sentiment d’appartenance, enjeux locaux structurants",
            "Tendance de vote": "Forte participation, fidélité politique",
            "Besoins": "Valorisation du territoire, patrimoine, sécurité, proximité",
            "Valeurs dominantes": "Appartenance, continuité, mémoire locale, transmission"
        }
    }

    const diplome = {
        "Sans diplôme": {
            "Mobilisation électorale": "Très faible",
            "Nuances du vote": "Abstention massive, RN dominant",
            "Tendance politique": "Rejet des partis traditionnels, vote protestataire",
            "Tendance de vote": "RN en tête, très faible confiance politique",
            "Besoins": "Emploi, pouvoir d’achat, logement social, accompagnement social",
            "Valeurs dominantes": "Reconnaissance, sécurité économique, défiance envers les élites, attachement local"
        },
        "CAP-BEP": {
            "Mobilisation électorale": "Faible à moyenne",
            "Nuances du vote": "RN, parfois LFI, rejet des élites",
            "Tendance politique": "Vote de protection, défiance envers les partis classiques",
            "Tendance de vote": "RN souvent en tête, abstention notable",
            "Besoins": "Stabilité professionnelle, logement, reconnaissance sociale",
            "Valeurs dominantes": "Protection, autorité, justice sociale, rejet des inégalités perçues"
        },
        "BAC": {
            "Mobilisation électorale": "Moyenne",
            "Nuances du vote": "Vote partagé : LFI, RN, Renaissance, EELV",
            "Tendance politique": "Fragmentation électorale forte",
            "Tendance de vote": "Haut niveau d’indécision, volatilité selon contexte",
            "Besoins": "Insertion professionnelle, pouvoir d’achat, mobilité sociale",
            "Valeurs dominantes": "Accès à la réussite, reconnaissance, autonomie, mixité sociale"
        },
        "Enseignement sup": {
            "Mobilisation électorale": "Forte à très forte",
            "Nuances du vote": "Centre, écologie, gauche progressiste",
            "Tendance politique": "LREM, EELV, PS, parfois LFI selon la génération",
            "Tendance de vote": "Vote pro-européen, progressiste, très mobilisé",
            "Besoins": "Reconnaissance, services publics, transition écologique",
            "Valeurs dominantes": "Innovation, mobilité, ouverture, diversité, engagement citoyen"
        }
    }

    const csp = {
        "Cadres, Prof. intel. sup.": {
            "Mobilisation électorale": "Très forte",
            "Nuances du vote": "Centre-gauche ; Écologie",
            "Tendance politique": "vote souvent pro-européen, progressiste, tendance à voter pour LREM, PS, EELV. Surreprésentation dans les métropoles.",
            "Tendance de vote": "Fragmentation entre partis progressistes, montée de l’écologie",
            "Besoins": "Innovation, services publics performants, transition écologique, qualité de vie",
            "Valeurs dominantes": "Progrès, innovation, ouverture, Europe, transition écologique"
        },
        "Prof. intermédiaires": {
            "Mobilisation électorale": "Moyenne à forte",
            "Nuances du vote": "Centre ; Écologie",
            "Tendance politique": "vote éclaté entre centre-droit (LREM), centre-gauche (PS), écologiste (EELV), parfois protestataire. Sensible au contexte territorial.",
            "Tendance de vote": "Stabilité, mais attention aux basculements populistes dans les zones tendues",
            "Besoins": "Stabilité économique, services publics de qualité, pouvoir d’achat, sentiment de sécurité dans les territoires",
            "Valeurs dominantes": "Équilibre, stabilité, services publics accessibles, justice sociale"
        },
        "Employés": {
            "Mobilisation électorale": "Moyenne",
            "Nuances du vote": "Gauche modérée, syndicalisme, vote protestataire",
            "Tendance politique": "vote historiquement à gauche, mais forte montée du RN en réaction au déclassement perçu.",
            "Tendance de vote": "Montée de l’abstention, progression des extrêmes",
            "Besoins": "Pouvoir d’achat, sécurité de l’emploi, services de proximité, sécurité physique et économique",
            "Valeurs dominantes": "Solidarité, protection sociale, lutte contre les inégalités, sécurité du quotidien"
        },
        "Ouvriers": {
            "Mobilisation électorale": "Faible",
            "Nuances du vote": "Extrême droite ; Abstention",
            "Tendance politique": "RN devenu premier parti ouvrier, déclin du vote PCF/PS, très forte abstention dans les zones désindustrialisées.",
            "Tendance de vote": "Historiquement à gauche, basculement massif vers l’extrême droite",
            "Besoins": "Emploi local, reconnaissance sociale, sécurité physique et économique",
            "Valeurs dominantes": "Protection, reconnaissance, identité nationale, sécurité physique et sociale"
        },
        "Agriculteurs exploitants": {
            "Mobilisation électorale": "Élevée mais très localisée",
            "Nuances du vote": "Droite modérée ; républicaine",
            "Tendance politique": "soutien traditionnel à LR, voire RN dans certaines zones, influence syndicale agricole (ex : FNSEA).",
            "Tendance de vote": "Stabilité à droite",
            "Besoins": "Soutien au monde rural, fiscalité adaptée, valorisation des territoires",
            "Valeurs dominantes": "Tradition, ancrage territorial, autonomie, conservatisme économique"
        },
        "Artisans, Comm., Chefs entr.": {
            "Mobilisation électorale": "Forte",
            "Nuances du vote": "Droite modérée ; républicaine ; libéralisme économique",
            "Tendance politique": "vote orienté vers LR ou LREM, mais progression du RN chez les indépendants précaires ou en difficulté.",
            "Tendance de vote": "Fidélité aux partis de droite traditionnelle, percées RN chez les indépendants précaires",
            "Besoins": "Demande de stabilité économique et de simplification, sécurité des biens et des activités",
            "Valeurs dominantes": "Liberté d’entreprendre, autonomie, sécurité des biens, rigueur fiscale"
        },
        "Retraités": {
            "Mobilisation électorale": "Très forte",
            "Nuances du vote": "Droite modérée ; républicaine ; conservatisme",
            "Tendance politique": "fidélité forte aux partis de gouvernement, attention portée à l’ordre, la santé et les retraites.",
            "Tendance de vote": "Maintien du vote conservateur",
            "Besoins": "Sensibilité accrue aux enjeux de santé, sécurité et pouvoir d’achat, insécurité ressentie",
            "Valeurs dominantes": "Ordre, stabilité, sécurité économique, conservatisme social"
        },
        "Autres personnes sans activité professionnelle": {
            "Mobilisation électorale": "Très faible à faible",
            "Nuances du vote": "Gauche radicale ou extrême droite",
            "Tendance politique": "vote protestataire très marqué, fort taux d’abstention, sensibilité aux discours de rupture sociale.",
            "Tendance de vote": "Très forte abstention, vote de rupture (LFI ou RN selon les contextes)",
            "Besoins": "Accès aux droits, accompagnement social, lutte contre l’exclusion, amélioration des conditions de vie",
            "Valeurs dominantes": "Assistance, justice sociale, défiance envers les élites, demande de reconnaissance"
        }
    }


    function getMax(item) {
        let max = 0;
        Object.keys(item).forEach((key) => {
            if (item[key].nb > max) {
                max = item[key].nb;
            }
        })
        return max;
    }

    function getMax2(items) {
        let max = 0;
        Object.keys(items).forEach((key) => {
            Object.keys(items[key]).forEach((key2) => {
                if (items[key][key2].nb > max) {
                    max = items[key][key2].nb;
                }
            })
        })
        return max;
    }

    const [expended1, setExpended1] = React.useState(false);
    const [expended2, setExpended2] = React.useState(false);
    const [expended3, setExpended3] = React.useState(false);
    React.useEffect(() => {
        if (forceOpen) {
            setExpended1(true);
            setExpended2(true);
            setExpended3(true);
        }
    }, [forceOpen]);

    React.useEffect(() => {
        if (forceClose) {
            setExpended1(true);
            setExpended2(true);
            setExpended3(true);
        }
    }, [forceClose]);

    const [goto, setGoto] = React.useState(null);
    React.useEffect(() => {
        if (goto) {
            document.getElementById(goto)?.scrollIntoView(true)
            setGoto(null)
        }
    }, [goto]);

    return (
        <Box sx={{width: '100%', display: "flex", flexDirection: "column", gap: "20px", mb: 2}}>
            <Box id={"soufflet1-" + bvId}/>

            {resultats && <Accordion
                square={'false'}
                sx={{
                    boxShadow: 3,
                    borderRadius: 2
                }}
                className="page-break2"
                expanded={expended1}
            >
                <AccordionSummary

                    expandIcon={<IconButton
                        component="a"
                        sx={{
                            color: "#ffff",
                            backgroundColor: "#ca0680",
                        }}
                        onClick={() => {
                            setExpended1(!expended1)
                            setExpended2(false)
                            setExpended3(false)
                            tddDelay(600).then(() => {
                                setGoto("soufflet1-" + bvId)
                            })
                        }}
                    >
                        <ExpandMoreIcon/>
                    </IconButton>}>
                    <Typography variant="h5"
                                sx={{
                                    color: "#302470",
                                    fontSize: "20px",
                                    fontStyle: "normal",
                                    fontWeight: 700,
                                    lineHeight: "32px"
                                }}>
                        {("Comportements électoraux")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>

                    <EvolutionTour1Tour2
                        title={"Evolution du nombre d'inscrits"}
                        data={resultats.inscrits2}
                        maxInscrit={getMax2(resultats.inscrits2)}
                        percentVisible={false}

                    />

                    <Box sx={{mb: 2}}/>
                    <EvolutionTour1Tour2
                        title={"Evolution Abstention Premier tour et Deuxième tour"}
                        data={resultats.abstentions}
                        maxInscrit={getMax2(resultats.abstentions)}
                        percentVisible={true}

                    />
                    {otherResults && <Box sx={{mt: 4}}>

                        <Typography variant={"h6"}
                                    sx={{
                                        color: "#000",
                                        fontSize: "20px",
                                        fontStyle: "normal",
                                        fontWeight: 700,
                                        lineHeight: "32px"
                                    }}>Evolution du taux d'abstentions série longue</Typography>
                        <Box sx={{p: 2}}>
                            <Box sx={{display: "flex", flexDirection: "row"}}>
                                <Box sx={{width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1}}>
                                    &nbsp;
                                </Box>
                                {Object.keys(otherResults).map((date) => {
                                    return Object.keys(otherResults[date]).map((election) => {
                                        return (<Box key={date + "_" + election} sx={{
                                            width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1, backgroundColor: "#e8eddf",
                                            display: "flex", flexDirection: "column", alignItems: "center"
                                        }}>
                                            <Typography
                                                sx={{
                                                    fontWeight: election === "muni" ? 700 : 200,
                                                    fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                                    lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                                }}>{getNomElection[election]}</Typography>
                                            <Typography
                                                sx={{
                                                    fontWeight: election === "muni" ? 700 : 200,
                                                    fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                                    lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                                }}>{date}</Typography>
                                        </Box>)
                                    })
                                })}
                            </Box>
                            <Box sx={{display: "flex", flexDirection: "row"}}>
                                <Box sx={{width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1}}>
                                    <Typography sx={{
                                        fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                        lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                    }}>{tours["t1"]}</Typography>
                                </Box>
                                {Object.keys(otherResults).map((date) => {

                                    return Object.keys(otherResults[date]).map((election) => {
                                        let info = otherResults[date][election];
                                        let value = ""
                                        if (info["t1"]) {
                                            value = formatPercentage(info["t1"]["abs"] / info["t1"]["ins"])
                                        }
                                        return (<Box key={date + "_" + election}
                                                     sx={{width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1}}>
                                            <Typography sx={{
                                                fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                                lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                            }}>{value}</Typography>
                                        </Box>)
                                    })
                                })}
                            </Box>
                            <Box sx={{display: "flex", flexDirection: "row"}}>
                                <Box sx={{width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1}}>
                                    <Typography sx={{
                                        fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                        lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                    }}>{tours["t2"]}</Typography>
                                </Box>
                                {Object.keys(otherResults).map((date) => {
                                    return Object.keys(otherResults[date]).map((election) => {
                                        let info = otherResults[date][election];
                                        let value = ""
                                        if (info["t2"]) {
                                            value = formatPercentage(info["t2"]["abs"] / info["t2"]["ins"])
                                        }
                                        return (<Box key={date + "_" + election}
                                                     sx={{width: {md: 65, lg: 70, xl: 95}, border: "solid 1px #000", p: 1}}>
                                            <Typography sx={{
                                                fontSize: {md: "10px", lg: "12px", xl: "16px"},
                                                lineHeight: {md: "10px", lg: "12px", xl: "16px"}
                                            }}>{value}</Typography>
                                        </Box>)
                                    })
                                })}
                            </Box></Box>
                    </Box>}
                    <Box sx={{mb: 2}}/>
                    <EvolutionTour1Tour2
                        title={"Evolution du nombre de bulletins blanc et nuls Premier tour et Deuxième tour"}
                        data={resultats.blancs}
                        maxInscrit={getMax2(resultats.blancs)}
                        percentVisible={true}
                    />

                </AccordionDetails>
            </Accordion>}
            <Box id={"soufflet2-" + bvId} className="page-break2"/>

            {resultsParCandidatsNuancesCommune && <Accordion
                square={'false'}
                sx={{
                    boxShadow: 3,
                    borderRadius: 2
                }}

                expanded={expended2}


            >
                <AccordionSummary
                    expandIcon={<IconButton
                        //href={"#soufflet2-"+bvId}
                        component="a"
                        sx={{
                            color: "#ffff",
                            backgroundColor: "#ca0680",
                        }}

                        onClick={() => {
                            setExpended1(false)
                            setExpended2(!expended2)
                            setExpended3(false)
                            tddDelay(600).then(() => {
                                setGoto("soufflet2-" + bvId)
                            })
                        }}
                        color="inherit">
                        <ExpandMoreIcon/>
                    </IconButton>}>

                    <Typography variant="h5"
                                sx={{
                                    color: "#302470",
                                    fontSize: "20px",
                                    fontStyle: "normal",
                                    fontWeight: 700,
                                    lineHeight: "32px"
                                }}>
                        {("Résultats par candidats/nuances")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{display: "flex", flexDirection: "column", gap: "20px"}}>
                    <Typography variant="h6" sx={{
                        color: "#000",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "32px"
                    }}>
                        {("Evolution de la Couleur politique du candidat élu | Série longue")}
                    </Typography>
                    <AnalyseElectionSerieLongue
                        candidat={true}
                        otherResults={otherResults}
                    />


                    <Typography sx={{
                        color: "#000",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "32px"
                    }}>

                        {("Par candidats")}
                    </Typography>
                    <AnalyseElectionParCandidat
                        abstentions={resultats.abstentions}
                        data={resultsParCandidatsNuancesCommune.candidats}
                        total={resultsParCandidatsNuancesCommune.exprime}
                        couleurPolitique={false}
                    />
                    <Box className="page-break2"/>
                    <Typography
                        variant="h6" sx={{
                        color: "#000",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: "700",
                        lineHeight: "32px"
                    }}>

                        <Typography variant="h6" sx={{
                            color: "#000",
                            fontSize: "20px",
                            fontStyle: "normal",
                            fontWeight: "700",
                            lineHeight: "32px"
                        }}>
                            {("Evolution des nuances politiques |  Série longue")}
                        </Typography>
                        <AnalyseElectionSerieLongue
                            candidat={false}
                            otherResults={otherResults}
                        />
                        {("Par nuance politique")}
                    </Typography>
                    <AnalyseElectionParCandidat
                        abstentions={resultats.abstentions}
                        data={resultsParCandidatsNuancesCommune.nuances}
                        total={resultsParCandidatsNuancesCommune.exprime}
                        otherResults={otherResults}
                        couleurPolitique={true}
                    />

                </AccordionDetails>
            </Accordion>}
            <Box id={"soufflet3-" + bvId} className="page-break2"/>
            {data && showPersonae && <Accordion

                square={'false'}
                sx={{
                    boxShadow: 3,
                    borderRadius: 2,
                }}
                expanded={expended3}
            >
                <AccordionSummary
                    expandIcon={<IconButton
                        //href={"#soufflet3-"+bvId}
                        component="a"
                        sx={{
                            color: "#ffff",
                            backgroundColor: "#ca0680",
                        }}

                        onClick={() => {
                            setExpended3(!expended3)
                            setExpended1(false)
                            setExpended2(false)
                            tddDelay(600).then(() => {
                                setGoto("soufflet3-" + bvId)
                            })
                        }}
                        color="inherit">
                        <ExpandMoreIcon/>
                    </IconButton>}>

                    <Typography variant="h5" sx={{
                        color: "#302470",
                        fontSize: "20px",
                        fontStyle: "normal",
                        fontWeight: 700,
                        lineHeight: "32px"
                    }}>
                        {("Personae")}
                    </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{display: "flex", flexDirection: "column", gap: 2}}>
                    {showIris && <Box sx={{display: "flex", flexDirection: "row", flexWrap: "wrap", gap: 2, m: 2}}>
                        <Typography sx={{
                            color: "#666",
                            fontSize: "16px",
                            fontStyle: "normal",
                            fontWeight: 400,
                            lineHeight: "24px"
                        }}>
                            Iris :
                        </Typography>
                        {data.listOfIris.map((codeiris) => {
                            let name = correctName(resultsGeomIrisByBvsSynthese[codeiris].irisName)

                            return (<Typography key={codeiris}
                                                sx={{
                                                    color: "#666",
                                                    fontSize: "16px",
                                                    fontStyle: "normal",
                                                    fontWeight: 400,
                                                    lineHeight: "24px"
                                                }}> {name} ({codeiris}) &nbsp;|</Typography>)
                        })}

                    </Box>}
                    <Typography sx={{
                        color: "#666",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "24px"
                    }}>
                        Source INSEE RP 2022 / Comparaison millésime 2022 / 2016
                    </Typography>
                    <Typography sx={{
                        color: "#1976d2",
                        fontSize: "16px",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "24px",
                        textTransform: "uppercase"
                    }}>
                        *Ces éléments constituent un "portrait" générique et ne sont pas spécifiques à votre territoire
                        sources Insee/ Cevipof
                    </Typography>
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"Population par tranche d'age"} data={data.popAge}
                                                displayAlert={true}
                                                n={2}
                                                t1={"Populations les plus représentées"}
                                                t2={"Populations les plus dynamiques"}
                                                second={true}
                                                info={trancheAge}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Habitants"}
                    />
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"Population par CSP"} data={data.popCsp} displayAlert={true}
                                                n={2}
                                                t1={"Populations les plus représentées"}
                                                t2={"Populations les plus dynamiques"}
                                                second={true}
                                                info={csp}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Habitants"}
                    />
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"Population par Niveau d'étude"} data={data.popEtu}
                                                displayAlert={true}
                                                n={2}
                                                t1={"Population de + de 15 ans les plus représentés"}
                                                t2={"Population de + de 15 ans les plus dynamiques"}
                                                second={true}
                                                info={diplome}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Habitants"}
                    />
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"ACTIF OCCUPÉS, INACTIFS, CHÔMEURS"}
                                                data={data.popRevenues}
                                                displayAlert={true}
                                                n={3}
                                                t1={"Population de + de 15 ans les plus représentés"}
                                                t2={"Population de + de 15 ans les plus dynamiques"}
                                                second={false}
                                                info={status}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Habitants"}
                    />
                    <Box sx={{mb: 2}} className="page-break2"/>
                    <Typography variant="h6">
                        {("Analyse par Logements")} : Source INSEE RP 2022 / Comparaison millésime
                        2022/2016
                    </Typography>
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"RESIDENCES PRINCIPALES / STATUT D'OCCUPATION"}
                                                data={data.lgtStatus}
                                                displayAlert={true}
                                                n={2}
                                                t1={"Statut d'occupation les plus représentés"}
                                                t2={"Statut d'occupation les plus dynamiques"}
                                                second={true}
                                                info={statusOcc}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Res Principales"}
                    />
                    <Box sx={{mb: 2}}/>
                    <PersonnasHighestAndDynamic title={"RESIDENCES PRINCIPALES / Période d'emménagement"}
                                                data={data.lgtPeriode} displayAlert={true}
                                                n={2}
                                                t1={"Statut d'occupation les plus représentés"}
                                                t2={"Statut d'occupation les plus dynamiques"}
                                                second={true}
                                                info={periodeEmmenagement}
                                                items={["Mobilisation électorale", "Nuances du vote", "Tendance politique", "Tendance de vote", "Besoins", "Valeurs dominantes"]}
                                                unit={"Res Principales"}
                    />
                </AccordionDetails>
            </Accordion>}


        </Box>
    );
}
